import { Skeleton, Tooltip, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { ceil } from 'lodash';
import { useTranslation } from 'react-i18next';
import { PromoCode, PromoCodeType } from 'src/types';

import { CurrencyCodes } from '../../types/users/currency';
import { formatCurrency } from '../../utils/formatters/currency';
import Iconify from '../iconify';

interface IProps {
  total: number;
  discount: number;
  appliedPromoCode?: PromoCode;
  isFreeTicket: boolean;
  fees?: number;
  isLoading?: boolean;
  currency?: CurrencyCodes;
}

export default function EventPriceCard({
  total,
  appliedPromoCode,
  fees,
  discount,
  isFreeTicket,
  isLoading,
  currency,
}: IProps) {
  const { t: tEvent } = useTranslation('event', { keyPrefix: 'event-price-card' });
  return (
    <Stack display="flex" flexDirection="column" gap="8px" sx={{ py: 2 }}>
      {appliedPromoCode && discount > 0 && (
        <Stack direction="row" alignContent="center" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '14px' }}>
            {tEvent('discount')}
            {appliedPromoCode.type === PromoCodeType.FlatAmount
              ? tEvent('flat-amount', { value: formatCurrency(appliedPromoCode.value, currency) })
              : tEvent('percentage', { value: appliedPromoCode.value })}
          </Typography>
          {isLoading ? (
            <Skeleton width="2rem" />
          ) : (
            <Typography variant="body1" sx={{ fontSize: '14px' }} color="#76ff00">
              {`(- ${formatCurrency(discount, currency)})`}
            </Typography>
          )}
        </Stack>
      )}
      {fees && !isFreeTicket && (
        <Stack direction="row" alignContent="center" justifyContent="space-between">
          <Tooltip
            title={tEvent('service-fees-tooltip')}
            enterTouchDelay={0}
            leaveTouchDelay={3000}
          >
            <Stack direction="row" alignItems="center" gap={1} sx={{ cursor: 'pointer' }}>
              <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '12px' }}>
                {tEvent('service-fees')}
              </Typography>
              <Iconify width={12} height={12} icon="solar:info-circle-linear" />
            </Stack>
          </Tooltip>

          {isLoading && !isFreeTicket ? (
            <Skeleton width="2rem" />
          ) : (
            <Typography variant="body1" sx={{ fontSize: '12px' }}>
              {formatCurrency(ceil(fees, 2), currency)}
            </Typography>
          )}
        </Stack>
      )}

      <Stack direction="row" alignContent="center" justifyContent="space-between">
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '18px' }}>
          {tEvent('total')}
        </Typography>
        {isLoading ? (
          <Skeleton width="2rem" />
        ) : (
          <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
            {isFreeTicket ? tEvent('free') : `${formatCurrency(ceil(total, 2), currency)} `}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}
